export type JourneyStepId =
  | 'subscribe'
  | 'allotment'
  | 'collection'
  | 'farm-logistics'
  | 'milking'
  | 'filling'
  | 'locking'
  | 'traceability'
  | 'cold-chain'
  | 'distributor'
  | 'delivery'
  | 'unlock'
  | 'enjoy'
  | 'proof';

export type JourneyStep = {
  id: JourneyStepId;
  title: string;
  description: string;
  detail: string;
  action?: string;
};

export type BottleState = 'empty' | 'filled' | 'locked' | 'delivered' | 'unlocked';

export const journeyStepsEn: JourneyStep[] = [
  { id: 'subscribe', title: 'Start Your Subscription', description: 'Choose your milk, delivery quantity, and slot', detail: 'Interactive demonstration — no payment is taken', action: 'Create demo subscription' },
  { id: 'allotment', title: 'Your Bottle Is Assigned', description: 'Bottle #7 is linked to your subscription', detail: 'Tap to assign the reusable bottle that stays with this journey', action: 'Assign Bottle #7' },
  { id: 'collection', title: 'Empty Bottle Collection', description: 'Your empty bottle is collected for its next journey', detail: 'Tap to hand Bottle #7 to collection', action: 'Hand over bottle' },
  { id: 'farm-logistics', title: 'Your Bottle Travels to the Farm', description: 'Bottle #7 moves from collection to the farm', detail: 'The consumer key does not travel with the bottle' },
  { id: 'milking', title: 'The Milking Session Begins', description: 'Farmer milking is recorded', detail: 'The farmer uses the one permitted keyless opening under recording', action: 'Start recording' },
  { id: 'filling', title: 'Now the Bottle Is Filled', description: 'Fresh milk moves from the stainless-steel bucket into Bottle #7', detail: 'Same recorded session', action: 'Fill Bottle #7' },
  { id: 'locking', title: 'The Bottle Is Locked', description: 'The steel cap is replaced and the green handle locks it', detail: 'After filling, only the consumer key can reopen Bottle #7', action: 'Lock Bottle #7' },
  { id: 'traceability', title: 'Bottle #7 Is Now Traceable', description: 'The subscription, recording, bottle, and order are connected', detail: 'Every bottle has a story — RightOZ keeps it connected' },
  { id: 'cold-chain', title: 'Cold Chain Starts', description: 'The locked bottle moves through cold storage and transport', detail: 'Cold chain maintained during the journey' },
  { id: 'distributor', title: 'The Bottle Reaches the Distributor', description: 'Bottle identity, order match, and recorded process can be checked', detail: 'Proof is checked, not just shown' },
  { id: 'delivery', title: 'Your Milk Arrives', description: 'The locked bottle reaches your doorstep', detail: 'Milk reaches your doorstep within 12–16 hours of milking' },
  { id: 'unlock', title: 'Only You Can Open It', description: 'Use your key to open Bottle #7', detail: '', action: 'Unlock Bottle #7' },
  { id: 'enjoy', title: 'Now Enjoy Your Milk', description: 'You did not just receive milk — you followed its journey', detail: 'Farm to home, with proof you can see' },
  { id: 'proof', title: "Your Milk's Proof", description: 'Your complete journey is connected in one view', detail: 'The journey is yours to see' },
];

export const journeyStepsHi: JourneyStep[] = [
  { id: 'subscribe', title: 'सदस्यता शुरू करें', description: 'अपना दूध, डिलीवरी की मात्रा और स्लॉट चुनें।', detail: 'इंटरेक्टिव डेमो — कोई भुगतान नहीं लिया जाएगा।', action: 'डेमो सदस्यता बनाएं' },
  { id: 'allotment', title: 'आपकी बोतल आवंटित हुई', description: 'बोतल #7 आपकी सदस्यता से जोड़ी गई है।', detail: 'इस यात्रा के लिए पुनः उपयोगी बोतल आवंटित करने के लिए टैप करें।', action: 'बोतल #7 आवंटित करें' },
  { id: 'collection', title: 'खाली बोतल संग्रह', description: 'आपकी खाली बोतल अगली यात्रा के लिए एकत्र की जाती है।', detail: 'बोतल #7 को संग्रह के लिए सौंपने के लिए टैप करें।', action: 'बोतल सौंपें' },
  { id: 'farm-logistics', title: 'आपकी बोतल फार्म की ओर जाती है', description: 'बोतल #7 संग्रह से फार्म तक पहुँचती है।', detail: 'उपभोक्ता की चाबी बोतल के साथ नहीं जाती।' },
  { id: 'milking', title: 'दुहाई का सत्र शुरू होता है', description: 'किसान जानवर के पास हाथ से दूध निकालकर स्टील की बाल्टी में भरता है।', detail: 'किसान रिकॉर्डिंग के दौरान एकमात्र अनुमत बिना चाबी के खोलने का उपयोग करता है।', action: 'रिकॉर्डिंग शुरू करें' },
  { id: 'filling', title: 'अब बोतल भरी जाती है', description: 'ताज़ा दूध स्टील की बाल्टी से बोतल #7 में डाला जाता है।', detail: 'यही रिकॉर्ड किया गया सत्र।', action: 'बोतल #7 भरें' },
  { id: 'locking', title: 'बोतल बंद की जाती है', description: 'स्टील का ढक्कन लगाया जाता है और हरे हैंडल से ताला लगता है।', detail: 'भरने के बाद केवल उपभोक्ता की चाबी बोतल #7 खोल सकती है।', action: 'बोतल #7 बंद करें' },
  { id: 'traceability', title: 'बोतल #7 अब ट्रेस होने योग्य है', description: 'सदस्यता, रिकॉर्डिंग, बोतल और ऑर्डर सभी जुड़े हैं।', detail: 'हर बोतल की एक कहानी है। RightOZ इसे जोड़े रखता है।' },
  { id: 'cold-chain', title: 'कोल्ड चेन शुरू होती है', description: 'बंद बोतल ठंडे भंडारण और परिवहन से गुज़रती है।', detail: 'यात्रा के दौरान कोल्ड चेन बनाए रखा जाता है।' },
  { id: 'distributor', title: 'बोतल वितरक तक पहुँचती है', description: 'बोतल की पहचान, ऑर्डर मिलान और रिकॉर्ड प्रक्रिया जाँची जा सकती है।', detail: 'प्रमाण दिखाया नहीं, जाँचा जाता है।' },
  { id: 'delivery', title: 'आपका दूध आ गया', description: 'बंद बोतल आपके दरवाज़े तक पहुँचती है।', detail: 'दूध दुहाई के 12–16 घंटे में आपके घर पहुँचता है।' },
  { id: 'unlock', title: 'केवल आप इसे खोल सकते हैं', description: 'बोतल #7 खोलने के लिए अपनी चाबी का उपयोग करें।', detail: '', action: 'बोतल #7 खोलें' },
  { id: 'enjoy', title: 'अब अपना दूध पिएं', description: 'आपने सिर्फ दूध नहीं लिया। आपने इसकी यात्रा को देखा।', detail: 'फार्म से घर तक, उस प्रमाण के साथ जो आप देख सकते हैं।' },
  { id: 'proof', title: 'आपके दूध का प्रमाण', description: 'आपकी पूरी यात्रा एक दृश्य में जुड़ी है।', detail: 'यह यात्रा आपकी है, देखने के लिए।' },
];

/** Alias kept for compatibility with existing imports */
export const journeySteps = journeyStepsEn;

export const getJourneySteps = (language: 'en' | 'hi'): JourneyStep[] =>
  language === 'hi' ? journeyStepsHi : journeyStepsEn;

export const bottleStateForStep = (step: number, unlocked: boolean, locked: boolean): BottleState => {
  if (unlocked) return 'unlocked';
  if (step === 10) return 'delivered';
  if (locked || step > 6) return 'locked';
  if (step >= 5) return 'filled';
  return 'empty';
};

export const isRecordingStep = (step: number) => step >= 4 && step <= 6;

export const isActionDrivenStep = (id: JourneyStepId) => ['subscribe', 'allotment', 'collection', 'milking', 'filling', 'locking', 'unlock'].includes(id);

export const journeyStateForStep = (step: number) => ({
  recordingStarted: step > 4,
  filled: step > 5,
  locked: step > 6,
  unlocked: step > 11,
});

export const formatRecordingDuration = (seconds: number) => [
  Math.floor(seconds / 3600),
  Math.floor(seconds / 60) % 60,
  seconds % 60,
].map((part) => String(part).padStart(2, '0')).join(':');
